import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { BookOpen, CheckCircle2, ArrowLeft, Lock } from 'lucide-react';
import { useStudentCourses } from '../../modules/learning/hooks/useStudentCourses';
import { getGradeSummary, getCourseProgress } from '../../modules/learning/api/progress';
import { listLessons, listPages } from '../../modules/learning/api/assetServiceClient';
import type { CourseOut } from '../../modules/learning/types/catalog';

/** Parse "Part-1" / "Part-2" from book string for sorting. */
function parsePartNumber(book: string | undefined): number {
  if (!book) return 0;
  const m = book.match(/Part-(\d+)/i);
  return m ? parseInt(m[1], 10) : 0;
}

/** Sort courses by unit_no then by Part-N from book. */
function sortParts(courses: CourseOut[]): CourseOut[] {
  return [...courses].sort((a, b) => {
    const ua = a.unit_no ?? 0;
    const ub = b.unit_no ?? 0;
    if (ua !== ub) return ua - ub;
    return parsePartNumber(a.book) - parsePartNumber(b.book);
  });
}

interface PartWithProgress {
  course: CourseOut;
  progress: {
    completed_pages: number;
    total_pages: number;
    status: 'not_started' | 'in_progress' | 'completed';
    last_page_id?: string;
  };
  isLocked: boolean;
}

export const GradePartsList: React.FC = () => {
  const { grade } = useParams<{ grade: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const studentId = user?.id ?? 'demo-student';

  const gradeNum = grade ? parseInt(grade, 10) : undefined;
  const { courses, loading: coursesLoading, error } = useStudentCourses(
    gradeNum !== undefined && !Number.isNaN(gradeNum) ? { grade: gradeNum } : undefined
  );

  const location = useLocation();
  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  /** Per-course progress when summary has no totals (frontend fallback) */
  const [computedProgressByCourseId, setComputedProgressByCourseId] = useState<
    Record<string, { completed_pages: number; total_pages: number; status: 'not_started' | 'in_progress' | 'completed'; last_page_id?: string }>
  >({});

  const validGrade = gradeNum !== undefined && !Number.isNaN(gradeNum);

  // Fetch progress summary from backend; refetch when navigating back to this route (e.g. from player)
  useEffect(() => {
    if (!validGrade) {
      setSummaryData([]);
      return;
    }
    setPagesLoading(true);
    (async () => {
      try {
        const summary = await getGradeSummary(gradeNum ?? 0);
        setSummaryData(Array.isArray(summary) ? summary : []);
        setComputedProgressByCourseId({});
      } catch (err) {
        console.error('[GradePartsList] failed to fetch grade summary', err);
        setSummaryData([]);
      } finally {
        setPagesLoading(false);
      }
    })();
  }, [validGrade, gradeNum, location.key]);

  // When summary has no/zero total for a course, compute from course structure + getCourseProgress
  useEffect(() => {
    if (!courses?.length) return;

    const summaryByCourseId = new Map<string, any>();
    summaryData.forEach((s) => {
      const key = s.course_id ?? s.id ?? s.courseId;
      if (key) summaryByCourseId.set(key, s);
    });

    const sortedCourses = sortParts(courses);
    const toCompute: CourseOut[] = [];
    for (const course of sortedCourses) {
      const s = summaryByCourseId.get(course.id);
      const total = s ? (s.total_pages ?? s.totalPages ?? 0) : 0;
      const alreadyComputed = computedProgressByCourseId[course.id];
      if (total === 0 && !alreadyComputed) toCompute.push(course);
    }

    if (toCompute.length === 0) return;

    let cancelled = false;
    (async () => {
      const next: typeof computedProgressByCourseId = {};
      for (const course of toCompute) {
        if (cancelled) return;
        try {
          const lessons = await listLessons(course.id);
          const pageIds: string[] = [];
          for (const lesson of lessons) {
            const pages = await listPages(lesson.id);
            pageIds.push(...pages.map((p) => p.id));
          }
          const prog = await getCourseProgress(course.id);
          const completedIds = prog?.completedPageIds ?? [];
          const totalPages = pageIds.length;
          const completedCount = pageIds.filter((id) => completedIds.includes(id)).length;
          const status: 'not_started' | 'in_progress' | 'completed' =
            totalPages > 0 && completedCount === totalPages
              ? 'completed'
              : completedCount > 0
                ? 'in_progress'
                : 'not_started';
          next[course.id] = {
            completed_pages: completedCount,
            total_pages: totalPages,
            status,
            last_page_id: prog?.lastPageId ?? undefined,
          };
        } catch {
          // leave this course with summary/default
        }
      }
      if (!cancelled) setComputedProgressByCourseId((prev) => ({ ...prev, ...next }));
    })();
    return () => {
      cancelled = true;
    };
  }, [courses, summaryData, computedProgressByCourseId]);

  // Merge courses with summary data (normalize camelCase/snake_case; use computed fallback when total is 0)
  const partsWithProgress = useMemo((): PartWithProgress[] => {
    if (!courses) return [];

    const summaryByCourseId = new Map<string, any>();
    summaryData.forEach((s) => {
      const key = s.course_id ?? s.id ?? s.courseId;
      if (key) summaryByCourseId.set(key, s);
    });

    const sortedCourses = sortParts(courses);
    const mergedParts: PartWithProgress[] = [];

    for (let i = 0; i < sortedCourses.length; i++) {
      const course = sortedCourses[i];
      const summary = summaryByCourseId.get(course.id);
      const computed = computedProgressByCourseId[course.id];

      let completed_pages: number;
      let total_pages: number;
      let status: 'not_started' | 'in_progress' | 'completed';
      let last_page_id: string | undefined;

      if (computed) {
        completed_pages = computed.completed_pages;
        total_pages = computed.total_pages;
        status = computed.status;
        last_page_id = computed.last_page_id;
      } else if (summary) {
        completed_pages = summary.completed_pages ?? summary.completedCount ?? 0;
        total_pages = summary.total_pages ?? summary.totalPages ?? 0;
        last_page_id = summary.last_page_id ?? summary.lastPageId;
        status =
          summary.status ??
          (total_pages > 0 && completed_pages === total_pages
            ? 'completed'
            : completed_pages > 0
              ? 'in_progress'
              : 'not_started');
      } else {
        completed_pages = 0;
        total_pages = 0;
        status = 'not_started';
      }

      const progress = { completed_pages, total_pages, status, last_page_id };

      // Lock logic: Part-1 is always unlocked, Part-N unlocked only if Part-(N-1) is completed
      const isLocked = i > 0 && mergedParts[i - 1].progress.status !== 'completed';

      mergedParts.push({
        course,
        progress,
        isLocked,
      });
    }

    return mergedParts;
  }, [courses, summaryData, computedProgressByCourseId]);

  const recommendedPart = useMemo(() => {
    for (const part of partsWithProgress) {
      if (!part.isLocked && part.progress.status !== 'completed') {
        return { course: part.course, status: part.progress.status };
      }
    }
    return null;
  }, [partsWithProgress]);

  const allCompleted = partsWithProgress.length > 0 && partsWithProgress.every((part) => part.progress.status === 'completed');

  const handlePartClick = (part: PartWithProgress) => {
    if (part.isLocked) {
      // Show toast/message about completing previous part
      console.warn('முந்தைய பகுதியை முடிக்க வேண்டும்');
      return;
    }

    const recommendedCourseId = recommendedPart?.course?.id ?? null;
    const recommendedPartLabel =
      recommendedPart?.course?.book ?? recommendedPart?.course?.title_ta ?? 'earlier part';
    navigate(`/student/courses/${part.course.id}`, {
      state: {
        recommendedCourseId,
        recommendedPartLabel,
        currentPartLabel: part.course.book ?? part.course.title_ta,
        lastPageId: part.progress.last_page_id
      },
    });
  };

  const loading = coursesLoading || pagesLoading;

  return (
    <Layout>
      <PageHeader
        title={`வகுப்பு ${grade ?? ''} — பகுதிகள்`}
        subtitle="Select a part to open the course"
        actions={
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => navigate('/student/courses')}
            aria-label="Back to Course"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Course
          </Button>
        }
      />

      {/* debug counts */}
      <div className="text-sm text-gray-500 dark:text-gray-400 mb-3 px-2">
        Courses: {courses?.length ?? 0} | Summary: {summaryData.length} | Parts: {partsWithProgress.length} {pagesLoading ? '(loading summary...)' : ''}
      </div>

      {!validGrade && (
        <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 text-amber-800 dark:text-amber-200">
          Invalid grade.
        </div>
      )}

      {validGrade && loading && (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      )}

      {validGrade && error && (
        <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {validGrade && !loading && !error && courses.length === 0 && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-8 text-center text-gray-600 dark:text-gray-400">
          No parts available for this grade.
        </div>
      )}

      {validGrade && !loading && !error && courses.length > 0 && (
        <>
          {allCompleted && (
            <div className="mb-4 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 px-4 py-3 text-green-800 dark:text-green-200 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 shrink-0" />
              All parts completed 🎉
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {partsWithProgress.map((part) => {
              const { course, progress, isLocked } = part;
              const isRecommended = recommendedPart?.course?.id === course.id && progress.status !== 'completed';
              const statusLabel = progress.status === 'completed' ? 'Completed' : progress.status === 'not_started' ? 'Not Started' : 'In Progress';
              const statusColor =
                progress.status === 'completed'
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
                  : progress.completed_pages > 0
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';

              return (
                <Card
                  key={course.id}
                  className={`transition-all duration-200 border-2 ${
                    isLocked
                      ? 'cursor-not-allowed opacity-60 grayscale'
                      : 'cursor-pointer hover:shadow-lg hover:scale-[1.02]'
                  } ${
                    isRecommended && !isLocked
                      ? 'border-blue-500 dark:border-blue-500 shadow-md shadow-blue-500/20'
                      : isLocked
                        ? 'border-gray-300 dark:border-gray-600'
                        : 'border-gray-200 dark:border-gray-700 hover:border-primary-500 dark:hover:border-primary-500'
                  }`}
                  onClick={() => !isLocked && handlePartClick(part)}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-3">
                      <div className={`p-3 rounded-xl ${isLocked ? 'bg-gray-100 dark:bg-gray-800' : 'bg-primary-100 dark:bg-primary-900/50'}`}>
                        {isLocked ? (
                          <Lock className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                        ) : (
                          <BookOpen className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <CardTitle className={`text-lg font-semibold ${isLocked ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'}`}>
                          {course.title_ta || 'Untitled'}
                        </CardTitle>
                        {isRecommended && !isLocked && (
                          <span className="inline-block mt-1 text-xs font-semibold text-blue-600 dark:text-blue-400">
                            Continue
                          </span>
                        )}
                        {isLocked && (
                          <span className="inline-block mt-1 text-xs font-medium text-gray-500 dark:text-gray-400">
                            முந்தைய பகுதியை முடிக்க வேண்டும்
                          </span>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2 text-sm text-gray-600 dark:text-gray-400">
                      {course.book && (
                        <span className={`px-2 py-1 rounded ${isLocked ? 'bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500' : 'bg-gray-100 dark:bg-gray-800'}`}>
                          {course.book}
                        </span>
                      )}
                      <span className={`px-2 py-1 rounded text-xs font-medium ${statusColor}`}>
                        {statusLabel}
                      </span>
                    </div>
                    <p className={`mt-2 text-sm tabular-nums ${isLocked ? 'text-gray-400 dark:text-gray-500' : 'text-gray-600 dark:text-gray-400'}`}>
                      {progress.completed_pages} / {progress.total_pages}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* {validGrade && !loading && !error && courses && courses.length > 0 && summaryData.length === 0 && !pagesLoading && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4 text-center text-gray-600 dark:text-gray-400">
          No progress data yet.
        </div>
      )} */}
    </Layout>
  );
};
